/**
 * A2A Admin Service
 * Wraps admin operations using A2A client instead of direct Supabase calls
 * 
 * NOTE: Backend A2A capabilities for Admin are not yet implemented.
 * This service is prepared for when backend capabilities are added.
 * For now, it will fall back to direct Supabase calls.
 */

import { A2AClient } from '../client.js';
import { supabaseAdmin } from '../../lib/supabase.js';
import { normalizeRole, isAdminRole } from '../../lib/roleUtils.js';

// Initialize A2A client
const a2aClient = new A2AClient(
  process.env.REACT_APP_A2A_BACKEND_URL || process.env.REACT_APP_API_URL,
  'myclass-client-admin',
  {
    secretKey: process.env.REACT_APP_A2A_SECRET_KEY,
    enableLogging: process.env.NODE_ENV === 'development'
  }
);

// Map tenant_id strings to actual UUID tenant IDs
const tenantIdMap = {
  'tn_01': 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3',
  'tn_02': '1acd60af-8884-48bb-b1ea-13fbb935d3a8',
  'tn_03': 'adf83c8b-b623-4f69-b65f-25e11c2781ce'
};

/**
 * A2A Admin Service
 * Currently uses direct Supabase until backend A2A capabilities are implemented
 */
export const a2aAdminService = {
  /**
   * List users
   * TODO: Migrate to A2A capability once backend implements list_users
   */
  async listUsers({ tenant_id, role = '', status = 'active' }) {
    try {
      const actualTenantId = tenantIdMap[tenant_id] || tenant_id;
      
      console.log(`[A2A Admin] Listing users for tenant ${tenant_id} -> ${actualTenantId}`);

      // TODO: Once backend implements list_users capability, use this:
      // const result = await a2aClient.executeTask('list_users', {
      //   tenant_id: actualTenantId,
      //   role,
      //   status
      // });
      // return {
      //   success: result.success,
      //   data: result.data || null,
      //   error: result.error || null
      // };

      // For now, use direct Supabase
      let query = supabaseAdmin
        .from('user_profiles')
        .select('*, auth_users:user_id(email, created_at)')
        .eq('tenant_id', actualTenantId);

      if (role) {
        query = query.eq('role', role);
      }

      if (status !== 'all') {
        query = query.eq('status', status === 'active' ? 'active' : 'inactive');
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) {
        console.error('[A2A Admin] Error listing users:', error);
        return { success: false, data: null, error: error.message };
      }

      // Format users data
      const users = (data || []).map(user => ({
        id: user.id,
        name: user.name || user.english_name || 'Unknown',
        email: user.auth_users?.email || user.email || '',
        role: user.role || 'student',
        status: user.status || 'active',
        created_at: user.created_at
      }));

      return { success: true, data: users, error: null };

    } catch (err) {
      console.error('[A2A Admin] Unexpected error:', err);
      return { success: false, data: null, error: err.message || 'Unexpected error' };
    }
  },

  /**
   * Get system settings
   * TODO: Migrate to A2A capability once backend implements get_system_settings
   */
  async getSystemSettings({ tenant_id }) {
    try {
      const actualTenantId = tenantIdMap[tenant_id] || tenant_id;
      
      // TODO: Once backend implements get_system_settings capability, use this:
      // const result = await a2aClient.executeTask('get_system_settings', {
      //   tenant_id: actualTenantId
      // });
      // return {
      //   success: result.success,
      //   data: result.data || null,
      //   error: result.error || null
      // };

      // For now, return default settings
      const defaultSettings = {
        general: {
          school_name: 'MyClass School',
          language: 'zh-TW',
          timezone: 'Asia/Hong_Kong'
        },
        security: {
          password_policy: 'standard',
          session_timeout: 3600,
          two_factor_enabled: false
        },
        notifications: {
          email_enabled: true,
          sms_enabled: false,
          push_enabled: true
        },
        integrations: {
          api_enabled: true,
          webhook_url: ''
        }
      };

      return { success: true, data: defaultSettings, error: null };

    } catch (err) {
      return { success: false, data: null, error: err.message };
    }
  },

  /**
   * Get system analytics
   * TODO: Migrate to A2A capability once backend implements get_system_analytics
   */
  async getSystemAnalytics({ tenant_id, start, end }) {
    try {
      const actualTenantId = tenantIdMap[tenant_id] || tenant_id;
      
      // TODO: Once backend implements get_system_analytics capability, use this:
      // const result = await a2aClient.executeTask('get_system_analytics', {
      //   tenant_id: actualTenantId,
      //   start_date: start,
      //   end_date: end
      // });
      // return {
      //   success: result.success,
      //   data: result.data || null,
      //   error: result.error || null
      // };

      // For now, return mock analytics
      const { data: users } = await supabaseAdmin
        .from('user_profiles')
        .select('id, status, created_at')
        .eq('tenant_id', actualTenantId);

      const totalUsers = users?.length || 0;
      const activeUsers = users?.filter(u => u.status === 'active').length || 0;

      const mockAnalytics = {
        total_users: totalUsers,
        active_users: activeUsers,
        usage_rate: 85,
        error_rate: 0.5,
        user_growth: [],
        feature_usage: [],
        performance: []
      };

      return { success: true, data: mockAnalytics, error: null };

    } catch (err) {
      return { success: false, data: null, error: err.message };
    }
  },

  /**
   * Create user
   * TODO: Migrate to A2A capability once backend implements create_user
   */
  async createUser({ tenant_id, userData }) {
    try {
      const actualTenantId = tenantIdMap[tenant_id] || tenant_id;
      
      console.log(`[A2A Admin] Creating user for tenant ${tenant_id} -> ${actualTenantId}`);

      // TODO: Once backend implements create_user capability, use this:
      // const result = await a2aClient.executeTask('create_user', {
      //   tenant_id: actualTenantId,
      //   ...userData
      // });
      // return {
      //   success: result.success,
      //   data: result.data || null,
      //   error: result.error || null
      // };

      // For now, use direct Supabase
      const { data, error } = await supabaseAdmin
        .from('user_profiles')
        .insert({
          tenant_id: actualTenantId,
          name: userData.name,
          english_name: userData.english_name,
          role: userData.role || 'student',
          status: userData.status || 'active',
          ...userData
        })
        .select()
        .single();

      if (error) {
        console.error('[A2A Admin] Error creating user:', error);
        return { success: false, data: null, error: error.message };
      }

      return { success: true, data, error: null };

    } catch (err) {
      console.error('[A2A Admin] Unexpected error:', err);
      return { success: false, data: null, error: err.message || 'Unexpected error' };
    }
  },

  /**
   * Update user
   * TODO: Migrate to A2A capability once backend implements update_user
   */
  async updateUser({ tenant_id, user_id, userData }) {
    try {
      const actualTenantId = tenantIdMap[tenant_id] || tenant_id;
      
      console.log(`[A2A Admin] Updating user ${user_id} for tenant ${tenant_id}`);

      // TODO: Once backend implements update_user capability, use this:
      // const result = await a2aClient.executeTask('update_user', {
      //   tenant_id: actualTenantId,
      //   user_id,
      //   ...userData
      // });
      // return {
      //   success: result.success,
      //   data: result.data || null,
      //   error: result.error || null
      // };

      // For now, use direct Supabase
      const { data, error } = await supabaseAdmin
        .from('user_profiles')
        .update(userData)
        .eq('id', user_id)
        .eq('tenant_id', actualTenantId)
        .select()
        .single();

      if (error) {
        console.error('[A2A Admin] Error updating user:', error);
        return { success: false, data: null, error: error.message };
      }

      return { success: true, data, error: null };

    } catch (err) {
      console.error('[A2A Admin] Unexpected error:', err);
      return { success: false, data: null, error: err.message || 'Unexpected error' };
    }
  },

  /**
   * Delete user
   * TODO: Migrate to A2A capability once backend implements delete_user
   */
  async deleteUser({ tenant_id, user_id }) {
    try {
      const actualTenantId = tenantIdMap[tenant_id] || tenant_id;
      
      console.log(`[A2A Admin] Deleting user ${user_id} for tenant ${tenant_id}`);

      // TODO: Once backend implements delete_user capability, use this:
      // const result = await a2aClient.executeTask('delete_user', {
      //   tenant_id: actualTenantId,
      //   user_id
      // });
      // return {
      //   success: result.success,
      //   data: result.data || null,
      //   error: result.error || null
      // };

      // For now, use direct Supabase (soft delete by setting status to inactive)
      const { data, error } = await supabaseAdmin
        .from('user_profiles')
        .update({ status: 'inactive', deleted_at: new Date().toISOString() })
        .eq('id', user_id)
        .eq('tenant_id', actualTenantId)
        .select()
        .single();

      if (error) {
        console.error('[A2A Admin] Error deleting user:', error);
        return { success: false, data: null, error: error.message };
      }

      return { success: true, data, error: null };

    } catch (err) {
      console.error('[A2A Admin] Unexpected error:', err);
      return { success: false, data: null, error: err.message || 'Unexpected error' };
    }
  },

  /**
   * Update system settings
   * TODO: Migrate to A2A capability once backend implements update_system_settings
   */
  async updateSystemSettings({ tenant_id, settings }) {
    try {
      const actualTenantId = tenantIdMap[tenant_id] || tenant_id;
      
      console.log(`[A2A Admin] Updating system settings for tenant ${tenant_id}`);

      // TODO: Once backend implements update_system_settings capability, use this:
      // const result = await a2aClient.executeTask('update_system_settings', {
      //   tenant_id: actualTenantId,
      //   settings
      // });
      // return {
      //   success: result.success,
      //   data: result.data || null,
      //   error: result.error || null
      // };

      // For now, store in a settings table or return success
      // This would typically be stored in a tenant_settings table
      console.log('[A2A Admin] Settings update (mock):', settings);
      
      return { success: true, data: settings, error: null };

    } catch (err) {
      console.error('[A2A Admin] Unexpected error:', err);
      return { success: false, data: null, error: err.message || 'Unexpected error' };
    }
  },

  /**
   * Export data
   * TODO: Migrate to A2A capability once backend implements export_data
   */
  async exportData({ tenant_id, dataType, format = 'json' }) {
    try {
      const actualTenantId = tenantIdMap[tenant_id] || tenant_id;
      
      console.log(`[A2A Admin] Exporting ${dataType} data for tenant ${tenant_id}`);

      // TODO: Once backend implements export_data capability, use this:
      // const result = await a2aClient.executeTask('export_data', {
      //   tenant_id: actualTenantId,
      //   data_type: dataType,
      //   format
      // });
      // return {
      //   success: result.success,
      //   data: result.data || null,
      //   error: result.error || null
      // };

      // For now, return mock export data
      let exportData = null;
      
      if (dataType === 'users') {
        const usersResult = await this.listUsers({ tenant_id, status: 'all' });
        exportData = usersResult.data || [];
      } else if (dataType === 'classes') {
        const { data } = await supabaseAdmin
          .from('saas_classes')
          .select('*')
          .eq('tenant_id', actualTenantId);
        exportData = data || [];
      }

      return { success: true, data: exportData, error: null };

    } catch (err) {
      console.error('[A2A Admin] Unexpected error:', err);
      return { success: false, data: null, error: err.message || 'Unexpected error' };
    }
  },

  /**
   * Import data
   * TODO: Migrate to A2A capability once backend implements import_data
   */
  async importData({ tenant_id, dataType, data, format = 'json' }) {
    try {
      const actualTenantId = tenantIdMap[tenant_id] || tenant_id;
      
      console.log(`[A2A Admin] Importing ${dataType} data for tenant ${tenant_id}`);

      // TODO: Once backend implements import_data capability, use this:
      // const result = await a2aClient.executeTask('import_data', {
      //   tenant_id: actualTenantId,
      //   data_type: dataType,
      //   data,
      //   format
      // });
      // return {
      //   success: result.success,
      //   data: result.data || null,
      //   error: result.error || null
      // };

      // For now, return mock import result
      console.log('[A2A Admin] Data import (mock):', { dataType, recordCount: data?.length || 0 });
      
      return { 
        success: true, 
        data: { 
          imported: data?.length || 0,
          failed: 0,
          message: 'Import completed successfully (mock)'
        }, 
        error: null 
      };

    } catch (err) {
      console.error('[A2A Admin] Unexpected error:', err);
      return { success: false, data: null, error: err.message || 'Unexpected error' };
    }
  },

  /**
   * Get audit logs
   * TODO: Migrate to A2A capability once backend implements get_audit_logs
   */
  async getAuditLogs({ tenant_id, start_date, end_date, action_type, user_id, limit = 100 }) {
    try {
      const actualTenantId = tenantIdMap[tenant_id] || tenant_id;
      
      console.log(`[A2A Admin] Fetching audit logs for tenant ${tenant_id}`);

      // TODO: Once backend implements get_audit_logs capability, use this:
      // const result = await a2aClient.executeTask('get_audit_logs', {
      //   tenant_id: actualTenantId,
      //   start_date,
      //   end_date,
      //   action_type,
      //   user_id,
      //   limit
      // });
      // return {
      //   success: result.success,
      //   data: result.data || null,
      //   error: result.error || null
      // };

      // For now, return mock audit logs
      // In production, this would query an audit_logs table
      const mockLogs = [
        {
          id: '1',
          tenant_id: actualTenantId,
          user_id: user_id || null,
          action_type: action_type || 'view',
          action: 'Viewed user list',
          resource_type: 'users',
          resource_id: null,
          ip_address: '192.168.1.1',
          user_agent: 'Mozilla/5.0',
          created_at: new Date().toISOString()
        },
        {
          id: '2',
          tenant_id: actualTenantId,
          user_id: user_id || null,
          action_type: 'create',
          action: 'Created new user',
          resource_type: 'user',
          resource_id: 'user_123',
          ip_address: '192.168.1.1',
          user_agent: 'Mozilla/5.0',
          created_at: new Date(Date.now() - 3600000).toISOString()
        }
      ];

      // Filter by action_type if provided
      let filteredLogs = mockLogs;
      if (action_type) {
        filteredLogs = mockLogs.filter(log => log.action_type === action_type);
      }

      // Filter by user_id if provided
      if (user_id) {
        filteredLogs = filteredLogs.filter(log => log.user_id === user_id);
      }

      // Apply limit
      filteredLogs = filteredLogs.slice(0, limit);

      return { success: true, data: filteredLogs, error: null };

    } catch (err) {
      console.error('[A2A Admin] Unexpected error:', err);
      return { success: false, data: null, error: err.message || 'Unexpected error' };
    }
  },

  /**
   * Get admin permissions
   * TODO: Migrate to A2A capability once backend implements get_admin_permissions
   */
  async getAdminPermissions({ user_id, tenant_id }) {
    try {
      const actualTenantId = tenantIdMap[tenant_id] || tenant_id;
      
      console.log(`[A2A Admin] Fetching admin permissions for user ${user_id}`);

      // TODO: Once backend implements get_admin_permissions capability, use this:
      // const result = await a2aClient.executeTask('get_admin_permissions', {
      //   tenant_id: actualTenantId,
      //   user_id
      // });
      // return {
      //   success: result.success,
      //   data: result.data || null,
      //   error: result.error || null
      // };

      // For now, check if user is admin and return default permissions
      const { data: userProfile, error } = await supabaseAdmin
        .from('user_profiles')
        .select('role, tenant_id, email')
        .eq('id', user_id)
        .eq('tenant_id', actualTenantId)
        .single();

      if (error || !userProfile) {
        console.error('[A2A Admin] Error fetching user profile:', error);
        return { success: false, data: [], error: error?.message || 'User not found' };
      }

      // Check if user is admin using role normalization
      const normalizedRole = normalizeRole(userProfile.role);

      if (!isAdminRole(normalizedRole)) {
        return { success: false, data: [], error: 'User is not an admin' };
      }

      // Default admin permissions
      const defaultPermissions = [
        'manage_users',
        'manage_classes',
        'manage_grades',
        'manage_attendance',
        'manage_system_settings',
        'view_analytics',
        'export_data',
        'import_data',
        'view_audit_logs',
        'manage_permissions'
      ];

      // Check for super admin (could be based on email, special flag, etc.)
      const originalRole = userProfile.role?.toLowerCase();
      const isSuperAdmin = originalRole === 'super_admin' || 
                          originalRole === 'superadmin' ||
                          userProfile.email?.includes('superadmin') ||
                          userProfile.email === 'admin@myclass.edu.hk';

      if (isSuperAdmin) {
        defaultPermissions.push('super_admin');
      }

      return { success: true, data: defaultPermissions, error: null };

    } catch (err) {
      console.error('[A2A Admin] Unexpected error:', err);
      return { success: false, data: [], error: err.message || 'Unexpected error' };
    }
  }
};

